import React, { useMemo, useState } from 'react'
import { FlatList, Pressable, StyleSheet } from 'react-native'
import { Controller, Control } from 'react-hook-form'

import {
  DatePicker,
  DNABox,
  DNAButton,
  DNACard,
  DNACheckbox,
  DNADivider,
  DNAModal,
  DNAText,
  Iffy,
  TextField,
} from '@alucio/lux-ui'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'
import { CommonIcon, DNAPopover } from 'src/components/DNA'

import { DNAModalVariant } from './DNAConnectedModal'
import { TrainingEventUser, UserStatus } from '@alucio/aws-beacon-amplify/src/models'
import { useUsersByTenantId } from 'src/state/redux/selector/user'
import { UserORM } from 'src/types/orms'
import { TrainingEventForm, useTrainingEventForm } from 'src/screens/Tenants/Tabs/Events/TrainingEventProvider'
import { useDispatch } from 'react-redux'
import { Input } from '@ui-kitten/components'
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal'
import TrainingEventConfirmationModal from './TrainingEventConfirmationModal'

const styles = StyleSheet.create({
  container: {
    maxHeight: 488,
  },
  wrapper: {
    marginHorizontal : 24,
  },
  selectAll: {
    paddingHorizontal: 6,
  },
  required: {
    color: colors['color-danger-500'],
    paddingHorizontal: 5,
  },
  title: {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    fontSize: 13,
  },
  searchBar: {
    backgroundColor: colors['color-text-white'],
    borderColor: colors['color-gray-100'],
  },
  shareModalHeader: { minWidth: 800 },
  footerWrapper: { width: '100%' },
})

interface FirstPageFooterType {
  toggleModal: () => void
  onNavigate: () => void
}

interface SecondPageFooterType {
  submitForm: () => void
  onNavigate: () => void
}

interface TrainingEventUsersListProps {
  tenantId: string
}
interface TrainingEventUserListRowProps {
  user: UserORM
  onPress: () => void
  isChecked: boolean
  control: Control<TrainingEventForm>
}

const TrainingEventUserListRow: React.FC<TrainingEventUserListRowProps> = (props) => {
  const { user, onPress, isChecked, control } = props
  return (
    <DNACard
      appearance="flat"
      interactive="pressableAlt"
      onPress={onPress}
    >
      <DNABox appearance="col">
        <DNADivider />
        <DNABox testID="search-team-user-list" spacing="between" alignY="center" style={{ padding: 6 }}>
          <DNABox appearance="row" spacing="sm" alignY="center" testID="search-team-result">
            <Controller
              name="selectedUsers"
              control={control}
              render={() => (
                <DNACheckbox
                  testID="search-team-result-checkbox"
                  checked={isChecked}
                  onChange={onPress}
                />
              )}
            />
            <DNABox pointerEvents="none" appearance="col">
              <DNAText
                bold
                numberOfLines={1}
                style={{ color: colors['color-gray-800'] }}
                testID="search-team-result-name"
              >
                {`${user.model.givenName} ${user.model.familyName} `}
              </DNAText>
              <DNAText
                style={{ color: colors['color-gray-400'] }}
                numberOfLines={1}
                testID="search-team-result-email"
              >
                {user.model.email}
              </DNAText>
            </DNABox>
          </DNABox>
        </DNABox>
      </DNABox>
    </DNACard>
  )
}

const FieldLabel: React.FC<{ label: string }> = ({ label }) => {
  return (
    <DNABox appearance="row">
      <DNAText style={styles.required}>*</DNAText>
      <DNAText style={styles.title}>{label}</DNAText>
    </DNABox>
  )
}

const CreateTrainingEventForm: React.FC = () => {
  const { rhForm } = useTrainingEventForm()
  const { control, formState } = rhForm
  return (
    <DNABox appearance="col" fill spacing="lg">
      {/* Event name */}
      <DNABox appearance="col" fill spacing="sm">
        <FieldLabel label={'Event name'} />
        <Controller
          name="title"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField.Kitten
              value={value}
              onChangeText={onChange}
            />
          )}
        />
        <Iffy is={formState.errors.title}>
          <DNAText status="danger">
            {formState.errors.title?.message}
          </DNAText>
        </Iffy>
      </DNABox>
      {/* start time */}
      <DNABox appearance="row" spacing="sm" childFill>
        <DNABox appearance="col" spacing="sm">
          <FieldLabel label={'Start date'} />
          <Controller
            name="startDateTime"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                selectedDate={value}
                onDateSelected={onChange}
                dateFormat="MM/dd/yyyy"
              />
            )}
          />
        </DNABox>
        <DNABox appearance="col" spacing="sm">
          <FieldLabel label={'Start time'} />
          <Controller
            name="startDateTime"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                selectedDate={value}
                onDateSelected={onChange}
                customFormat="h:mm aa z"
                showTimeSelectOnly={true}
                includeTimePicker={true}
                timeIntervals={30}
              />
            )}
          />
        </DNABox>
      </DNABox>
      {/* end time */}
      <DNABox appearance="row" spacing="sm" childFill>
        <DNABox appearance="col" spacing="sm">
          <FieldLabel label={'End date'} />
          <Controller
            name="endDateTime"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                selectedDate={value}
                onDateSelected={onChange}
                dateFormat="MM/dd/yyyy"
              />
            )}
          />
        </DNABox>
        <DNABox appearance="col" spacing="sm" style={{ marginBottom: 224 }}>
          <FieldLabel label={'End time'} />
          <Controller
            name="endDateTime"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                selectedDate={value}
                onDateSelected={onChange}
                customFormat="h:mm aa z"
                showTimeSelectOnly={true}
                includeTimePicker={true}
                timeIntervals={30}
              />
            )}
          />
          <Iffy is={formState.errors.endDateTime}>
            <DNAText status="danger">
              {formState.errors.endDateTime?.message}
            </DNAText>
          </Iffy>
        </DNABox>
      </DNABox>
    </DNABox>
  )
}

const TrainingEventUsersList: React.FC<TrainingEventUsersListProps> = (props) => {
  const { tenantId } = props
  const [searchText, setSearchText] = useState<string>('');
  const { rhForm } = useTrainingEventForm()

  const {
    control,
    getValues,
    setValue,
    watch,
  } = rhForm

  const tenantUsers = useUsersByTenantId(tenantId);
  const selectedUsers = watch('selectedUsers')
  const sortedTenantUsers = tenantUsers.filter(user => user.model.status === UserStatus.ACTIVE).sort((a, b) => {
    const aName  = a.model.givenName + ' ' + a.model.familyName;
    const bName  = b.model.givenName + ' ' + b.model.familyName;
    return aName.toLocaleLowerCase().localeCompare(bName.toLocaleLowerCase());
  }) || [];

  const filteredUsers = searchText ? sortedTenantUsers.filter((user) => {
    const lowerCasedText = searchText.toLocaleLowerCase();
    return user.model.familyName.toLocaleLowerCase().includes(lowerCasedText) ||
        user.model.givenName.toLocaleLowerCase().includes(lowerCasedText) ||
        user.model.email.toLocaleLowerCase().includes(lowerCasedText)
  }) : sortedTenantUsers;

  const toggleCheckedItem = (item: UserORM) => {
    const selectedUsers = getValues('selectedUsers')
    const userIndex = selectedUsers.findIndex(selectedUser => selectedUser.userId === item.model.id )
    if (userIndex > -1) {
      selectedUsers.splice(userIndex, 1)
      setValue('selectedUsers', selectedUsers.filter(user => user.userId !== item.model.id), { shouldDirty: true })
    } else {
      selectedUsers.push({
        userId: item.model.id,
        email: item.model.email,
        givenName: item.model.givenName,
        familyName: item.model.familyName,
      })
      setValue('selectedUsers', selectedUsers, { shouldDirty: true })
    }
  }

  const onPressSelectAll = () => {
    if (sortedTenantUsers.length === selectedUsers.length) {
      setValue('selectedUsers', [], { shouldDirty: true })
    } else {
      const formatUsers: TrainingEventUser[] = sortedTenantUsers.map(user => {
        return {
          userId: user.model.id,
          email: user.model.email,
          givenName: user.model.givenName,
          familyName: user.model.familyName,
        }
      })
      setValue('selectedUsers', formatUsers, { shouldDirty: true })
    }
  }

  const isSelectAllChecked = useMemo(() => (
    sortedTenantUsers.length === selectedUsers.length
  ), [sortedTenantUsers, selectedUsers])

  const renderCloseIcon = () => (
    <Iffy is={searchText}>
      <Pressable onPress={() => setSearchText('')}>
        <CommonIcon variant="close-circle" />
      </Pressable>
    </Iffy>
  )

  return (
    <DNABox appearance="col" spacing="md" fill style={styles.wrapper}>
      <DNAText bold>{selectedUsers.length} selected</DNAText>
      <DNABox appearance="col" fill style={styles.container} spacing="md">
        <Input
          placeholder="Search names or emails"
          value={searchText}
          onChangeText={setSearchText}
          accessoryRight={renderCloseIcon}
          style={styles.searchBar}
          testID="text-input"
        />
        <DNABox appearance="row" spacing="sm" alignY="center" style={styles.selectAll}>
          <DNACheckbox
            checked={isSelectAllChecked}
            onChange={onPressSelectAll}
            disabled={false}
          />
          <DNAText
            bold
            numberOfLines={1}
            style={{ color: colors['color-gray-800'] }}
          >
            Select all
          </DNAText>
        </DNABox>
        <DNABox appearance="col" style={{ height: 370 }}>
          <Controller
            name="selectedUsers"
            control={control}
            render={({ field: { value } }) => (
              <FlatList
                data={filteredUsers}
                scrollEnabled
                keyExtractor={(item) => item.model.id}
                renderItem={(row) => {
                  const isChecked = value.some(user => user.userId === row.item.model.id)
                  return (
                    <TrainingEventUserListRow
                      user={row.item}
                      onPress={() => toggleCheckedItem(row.item)}
                      isChecked={isChecked}
                      control={control}
                    />
                  )
                }}
              />
            )}
          />
        </DNABox>
      </DNABox>
    </DNABox>
  )
}

const FormFooter: React.FC<FirstPageFooterType> = (props) => {
  const { toggleModal, onNavigate } = props
  const { rhForm } = useTrainingEventForm()
  const { formState } = rhForm
  return (
    <DNABox appearance="row" spacing="sm" alignX="end" fill>
      <DNAButton
        testID="cancel-button"
        appearance="outline"
        status="tertiary"
        onPress={toggleModal}
        children="Cancel"
      />
      <DNAPopover disablePopover={formState.isValid}>
        <DNAPopover.Anchor>
          <DNAButton
            testID="share-folder-modal-continue-button"
            status="primary"
            onPress={onNavigate}
            disabled={!formState.isValid}
            children="Continue"
          />
        </DNAPopover.Anchor>
        <DNAPopover.Content offset={2}>
          <DNAText colorMode="dark">Please select all required fields</DNAText>
        </DNAPopover.Content>
      </DNAPopover>
    </DNABox>
  )
}

const UsersListFooter: React.FC<SecondPageFooterType> = (props) => {
  const { submitForm, onNavigate } = props
  const { rhForm } = useTrainingEventForm()
  const { watch } = rhForm

  const isUserSelected = watch('selectedUsers').length > 0

  return (
    <DNABox appearance="row" spacing="between" fill>
      <DNAButton
        testID="cancel-button"
        appearance="outline"
        status="tertiary"
        onPress={onNavigate}
        children="Back"
      />
      <DNAPopover disablePopover={isUserSelected}>
        <DNAPopover.Anchor>
          <DNAButton
            testID="share-folder-modal-continue-button"
            status="primary"
            onPress={submitForm}
            disabled={!isUserSelected}
            children="Done"
          />
        </DNAPopover.Anchor>
        <DNAPopover.Content offset={2}>
          <DNAText colorMode="dark">Please select at least one user</DNAText>
        </DNAPopover.Content>
      </DNAPopover>
    </DNABox>
  )
}

const DNATrainingEventModal: DNAModalVariant<{tenantId: string}> = (props) => {
  const { toggleModal, tenantId } = props
  const { rhForm, submitForm } = useTrainingEventForm()
  const dispatch = useDispatch();
  const {
    handleSubmit,
    formState: { isDirty },
  } = rhForm
  const [showFrom, setShowForm] = useState<boolean>(true)
  const onNavigate = () => {
    setShowForm(!showFrom)
  }

  const toggleModalModified = () => {
    if (isDirty) {
      dispatch(DNAModalActions.pushModal(TrainingEventConfirmationModal))
    } else {
      toggleModal()
    }
  }

  return (
    <DNABox>
      <DNAModal>
        <DNAModal.Header>
          <DNABox
            testID="share-folder-modal-header"
            appearance="row"
            spacing="between"
            style={styles.shareModalHeader}
            alignY="center"
          >
            <DNAText h4>Create event</DNAText>
            <DNAButton
              testID="cancel-icon-button"
              appearance="ghost"
              status="subtle"
              iconLeft="x"
              onPress={toggleModalModified}
            />
          </DNABox>
        </DNAModal.Header>
        <DNAModal.Body>
          {
            showFrom
              ? <CreateTrainingEventForm />
              : <TrainingEventUsersList
                  tenantId={tenantId}
              />
          }
        </DNAModal.Body>
        <DNAModal.Footer>
          <DNABox appearance="row" style={styles.footerWrapper}>
            {showFrom
              ? <FormFooter toggleModal={toggleModalModified} onNavigate={onNavigate} />
              : <UsersListFooter onNavigate={onNavigate} submitForm={handleSubmit(submitForm)} /> }
          </DNABox>
        </DNAModal.Footer>
      </DNAModal>
    </DNABox>
  )
}

export default DNATrainingEventModal
